import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import '../css/About.css';

const API_ENDPOINTS = {
  Gene: '/api/gene/counts/',
  Nucleotide: '/api/nucleotide/counts/',
  Taxonomy: '/api/taxonomy/counts/',
  GenomeAssembly: '/api/genomeassembly/counts/',
  PubMed: '/api/pubmed/counts/',
  Protein: '/api/protein/counts/',
  Bookshelf: '/api/book/counts/',
  GeoDataset: '/api/geo/counts/',
  ProteinGroup: '/api/proteinuniprot/counts/',
  BioAssay: '/api/bioassay/counts/',
  Pathway: '/api/pathway/counts/',
  Substance: '/api/substance/counts/',
  SRA: '/api/sra/counts/',
  BioProject: '/api/bioproject/counts/',
  BioSample: '/api/biosample/counts/',
  PMCArticle: '/api/pmc/counts/',
};

const tools = [
  { label: 'BLAST', path: '/blast' },

  {
    label: 'Primer3Plus',
    action: () =>
      window.open('https://www.primer3plus.com/index.html', '_blank'),
  },

  {
    label: 'Clustal Omega',
    action: () =>
      window.open('https://www.ebi.ac.uk/Tools/msa/clustalo/', '_blank'),
  },

  { label: 'JBrowse 2', action: 'jbrowse' },
];

const About = () => {
  const [counts, setCounts] = useState({});
  const [showTool, setShowTool] = useState(null);

  useEffect(() => {
    const fetchCounts = async () => {
      const updatedCounts = {};
      for (const key in API_ENDPOINTS) {
        try {
          const response = await axios.get(API_ENDPOINTS[key]);
          updatedCounts[key] =
            response.data?.count ?? response.data?.total_count ?? 0;
        } catch {
          updatedCounts[key] = 0;
        }
      }
      setCounts(updatedCounts);
    };

    fetchCounts();
  }, []);

  const renderFullScreenTool = () => {
    if (showTool !== 'jbrowse') return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: '#000',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 20px',
            backgroundColor: '#1a1a1a',
            color: 'white',
          }}
        >
          <h3 style={{ margin: 0 }}>🧬 JBrowse 2 Genome Viewer</h3>

          <button
            onClick={() => setShowTool(null)}
            style={{
              background: '#4CAF50',
              border: 'none',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '14px',
            }}
          >
            ⬅ Back
          </button>
        </div>

        <iframe
          src="/jbrowse-data/?config=config.json"
          title="JBrowse 2"
          style={{
            flexGrow: 1,
            width: '100%',
            height: '100%',
            border: 'none',
          }}
        />
      </div>
    );
  };

  if (showTool === 'jbrowse') return renderFullScreenTool();

  return (
    <div className="about-container">
      <h2 className="about-title">
        <b>
          <u>
            About – <i>TefDB</i>
          </u>
        </b>
      </h2>

      {/* Images section */}
      <div className="about-images">
        <div className="image-card">
          <img
            src="/images/tef_image2.jpg"
            alt="Eragrostis tef plant"
            className="about-image"
          />
          <p className="image-caption">
            <b>Eragrostis tef plant</b>
          </p>
        </div>

        <div className="image-card">
          <img
            src="/images/injera.jpeg"
            alt="Injera – traditional tef food"
            className="about-image"
          />
          <p className="image-caption">
            <b>Injera – traditional tef food</b>
          </p>
        </div>
      </div>

      <p className="about-description">
        <strong>
          <i>TefDB</i>
        </strong>{' '}
        is a curated biological database for <i>Eragrostis tef</i>,
        consolidating genomic, proteomic, transcriptomic, and literature
        resources into a unified platform for research and crop improvement.
      </p>

      <h3 className="section-title">Integrated Tools</h3>

      <ul className="about-list">
        {tools.map((tool, i) => (
          <li key={i}>
            {tool.path ? (
              <b>
                <Link to={tool.path}>{tool.label}</Link>
              </b>
            ) : typeof tool.action === 'function' ? (
              <b>
                <span
                  style={{ cursor: 'pointer', color: '#1a73e8' }}
                  onClick={tool.action}
                >
                  {tool.label}
                </span>
              </b>
            ) : (
              <b>
                <span
                  style={{ cursor: 'pointer', color: '#1a73e8' }}
                  onClick={() => setShowTool(tool.action)}
                >
                  {tool.label}
                </span>
              </b>
            )}
            {' '}– click to use tool
          </li>
        ))}
      </ul>

      <h3 className="section-title">Purpose</h3>
      <p className="about-text">
        <b>TefDB</b> centralizes tef biological resources, enabling easy
        exploration, analysis, and application in academic research and crop
        improvement.
      </p>

      <h3 className="section-title">Data Summary</h3>

      {['Genomes', 'Genes', 'Proteins', 'PubChem', 'Literatures'].map(
        (section) => (
          <div key={section}>
            <h4>{section}</h4>
            <table className="data-table">
              <tbody>
                {section === 'Genomes' && (
                  <>
                    <tr>
                      <td>
                        <Link to="/genomeassembly">Assembly</Link>
                      </td>
                      <td>{counts.GenomeAssembly}</td>
                    </tr>
                    <tr>
                      <td>
                        <Link to="/bioproject">BioProject</Link>
                      </td>
                      <td>{counts.BioProject}</td>
                    </tr>
                    <tr>
                      <td>
                        <Link to="/biosample">BioSample</Link>
                      </td>
                      <td>{counts.BioSample}</td>
                    </tr>
                    <tr>
                      <td>
                        <Link to="/nucleotide">Nucleotide</Link>
                      </td>
                      <td>{counts.Nucleotide}</td>
                    </tr>
                    <tr>
                      <td>
                        <Link to="/sra">SRA</Link>
                      </td>
                      <td>{counts.SRA}</td>
                    </tr>
                    <tr>
                      <td>
                        <Link to="/taxonomy">Taxonomy</Link>
                      </td>
                      <td>{counts.Taxonomy}</td>
                    </tr>
                  </>
                )}

                {section === 'Genes' && (
                  <>
                    <tr>
                      <td>
                        <Link to="/gene">Gene</Link>
                      </td>
                      <td>{counts.Gene}</td>
                    </tr>
                    <tr>
                      <td>
                        <Link to="/geodata">GEO</Link>
                      </td>
                      <td>{counts.GeoDataset}</td>
                    </tr>
                  </>
                )}

                {section === 'Proteins' && (
                  <>
                    <tr>
                      <td>
                        <Link to="/protein">Protein sequences</Link>
                      </td>
                      <td>{counts.Protein}</td>
                    </tr>
                    <tr>
                      <td>
                        <Link to="/proteinuniprot">
                          From UniProt
                        </Link>
                      </td>
                      <td>{counts.ProteinGroup}</td>
                    </tr>
                  </>
                )}

                {section === 'PubChem' && (
                  <>
                    <tr>
                      <td>
                        <Link to="/bioassay">Bioassay</Link>
                      </td>
                      <td>{counts.BioAssay}</td>
                    </tr>
                    <tr>
                      <td>
                        <Link to="/pathway">Pathways</Link>
                      </td>
                      <td>{counts.Pathway}</td>
                    </tr>
                    <tr>
                      <td>
                        <Link to="/substance">Substance</Link>
                      </td>
                      <td>{counts.Substance}</td>
                    </tr>
                  </>
                )}

                {section === 'Literatures' && (
                  <>
                    <tr>
                      <td>
                        <Link to="/pubmed">PubMed</Link>
                      </td>
                      <td>{counts.PubMed}</td>
                    </tr>
                    <tr>
                      <td>
                        <Link to="/book">Bookshelf</Link>
                      </td>
                      <td>{counts.Bookshelf}</td>
                    </tr>
                    <tr>
                      <td>
                        <Link to="/pmc">PMC</Link>
                      </td>
                      <td>{counts.PMCArticle}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        )
      )}

      <h3 className="section-title">Acknowledgements</h3>

      <p className="about-text">
        <b>Sponsors:</b>{' '}
        <a href="https://www.aau.edu.et/" target="_blank" rel="noopener noreferrer">
          AAU
        </a>,{' '}
        <a href="https://www.aastu.edu.et/" target="_blank" rel="noopener noreferrer">
          AASTU
        </a>,{' '}
        <a href="https://www.betin.gov.et/" target="_blank" rel="noopener noreferrer">
          BETin
        </a>
        <br />
        <b>Team Members:</b>{' '}
        <Link to="/team">
          Mr. Badasa Firdisa, Dr. Abiy Zegeye, Dr. Adugna Abdi
        </Link>
      </p>

      <p className="about-text">
        Data is sourced from public repositories including NCBI SRA, GEO,
        BioSample, BioProject, PubMed, UniProt, and EnsemblPlants.
      </p>

      <div className="about-mission">
        <i>
          “A unified resource for advancing tef genomics and scientific
          discovery.”
        </i>
      </div>
    </div>
  );
};

export default About;
